/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-observers */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import DataTypes from '@linc-technologies/linced-common/enums/data-type';
import InputTypes from '@linc-technologies/linced-common/enums/input-type';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import type ValueListItem from '@linc-technologies/linced-common/models/dynamic-field/value-list-item';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import { observes } from '@ember-decorators/object';
import type Heading from 'linced-admin/models/school-options/heading';
import type Set from 'linced-admin/models/school-options/set';

// Proto: schools.Option.
export default class Field extends Model {
  declare [Type]: 'school-options/field';

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @belongsTo('school-options/heading', { async: true, inverse: null })
  declare headingId: AsyncBelongsTo<Heading>;

  @attr('number', { defaultValue: 0 })
  declare inputType: number;

  @attr('number', { defaultValue: 0 })
  declare dataType: number;

  @fragment('dynamic-field/value')
  declare default: Value | null;

  @attr('string')
  declare placeholder: string | undefined;

  @fragmentArray('dynamic-field/value-list-item')
  declare options: FragmentArray<ValueListItem>;

  @attr({ defaultValue: () => ({}) })
  declare htmlAttributes: Record<string, string>;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @hasMany('school-options/set', { async: true, inverse: null })
  declare optionSetIds: AsyncHasMany<Set>;

  @computed('optionSetIds.content.[]')
  get optionSetsArray() {
    return resolved(this.optionSetIds);
  }

  @attr('string', { defaultValue: '' })
  declare staticContent: string;

  // Set datatype when input type changes
  @observes('inputType')
  _setDataType() {
    let dataType;
    switch (this.inputType) {
      case InputTypes.CHECKBOX: // Checkbox(es)
      case InputTypes.MULTI_SELECT: // MultiSelect (Chips)
        dataType = DataTypes.LIST; // List
        break;
      case InputTypes.NUMBER: // Number
        dataType = DataTypes.INTEGER; // Int (need to allow this to be set to 4 - Float)
        break;
      case InputTypes.SWITCH: // Switch
        dataType = DataTypes.BOOLEAN; // Bool
        break;
      case InputTypes.EDITABLE_LIST: // Editable List
        dataType = DataTypes.VALUE_LIST; // ValueList
        break;
      case InputTypes.WYSIWYG_EDITOR: // WYSIWYG Editor
        dataType = DataTypes.HTML; // HTML
        break;
      default:
        dataType = DataTypes.STRING; // String
        break;
    }

    this.set('dataType', dataType);
  }

  // Update default value fragment's datatype when data type is changed
  @observes('dataType')
  _updateDefaultDatatype() {
    if (!isEmpty(this.default)) {
      this.default!.set('datatype', this.dataType);
    }
  }
}
